import React, { useState } from "react";
import styled from "styled-components";
import { Input, Button, Typography, Select, Table, Toggle } from "../../../../../design-system";
import { colors } from "../../../../../design-system/theme/colors";
import { formatDocument, formatPhoneNumber } from "../../../../../utils";
import { downloadCSV } from "../../../../../utils";
import { getClients } from "../../../../../api/clients";
import Modal from "../../../../../_components/Modal";
import { CreateClientContainer } from "../../Client";

const FilterClients = ({ onClick, showTitle, modalBehavior }) => {
  const [params, setParams] = useState({ end_count: 10 });
  const [searchResult, setSearchResult] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastFieldUsed, setLastFieldUsed] = useState("");
  const [isToggled, setIsToggled] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const fields = [
    { name: "name", label: "Nome", type: "text", cyTest: "#filter-client-input-name" },
    { name: "cpf_cnpj", label: "CPF/CNPJ", type: "text", maxLength: 18, cyTest: "#filter-client-input-cpf-cnpj" },
    { name: "email", label: "E-mail", type: "email", cyTest: "#filter-client-input-email" },
    { name: "phone_number", label: "Telefone", type: "telefone", cyTest: "#filter-client-input-phone-number" },
    {
      name: "end_count",
      label: "Exibir até:",
      type: "select",
      options: [
        { label: "10 resultados", value: 10 },
        { label: "20 resultados", value: 20 },
        { label: "30 resultados", value: 30 },
        { label: "50 resultados", value: 50 },
        { label: "100 resultados", value: 100 },
        { label: "150 resultados", value: 150 },
      ],
    },
  ];

  const handleToggle = () => setIsToggled(!isToggled);

  const onChange = (e) => {
    let { name: key, value } = e.target;
    if (key === "phone_number" || key === "cpf_cnpj") {
      value = value.replace(/\D+/g, "");
    }
    setParams((prev) => ({ ...prev, [key]: value }));
    setLastFieldUsed(key);
  };

  const handleSetFocusOnLastFieldUsed = () => {
    if (lastFieldUsed) {
      try {
        const el = document.querySelector(`[name="${lastFieldUsed}"]`);
        if (el) el.focus();
      } catch (error) {}
    }
  };

  const filter = async () => {
    setLoading(true);
    setSearchResult([]);

    try {
      const results = await getClients(params);
      setSearchResult(results);
      setLoading(false);
      setTimeout(handleSetFocusOnLastFieldUsed, 100);
    } catch (error) {
      setLoading(false);
      setTimeout(handleSetFocusOnLastFieldUsed, 100);
    }
  };

  const onRowClick = (item) => {
    onClick(item);
    setSearchResult([]);
  };

  React.useEffect(() => {
    const handleExportCSV = async () => {
      setLoading(true);
      try {
        const results = await getClients({ ...params, end_count: 999999 });
        const headers = [
            "NOME", "RAZÃO SOCIAL", "NOME FANTASIA", "INSCRIÇÃO ESTADUAL", 
            "CNPJ", "CPF", "TELEFONE", "EMAIL", "CEP", "NUMERO", 
            "ESTADO", "CIDADE", "BAIRRO", "RUA", "COMPLEMENTO"
        ];
        
        const rows = results.map(c => {
            const getVal = (val) => val ? String(val).replace(/;/g, ',') : "";
            
            const nome = getVal(c.name);
            const razao_social = getVal(c.company && c.company.company_name);
            const nome_fantasia = getVal(c.company && c.company.fancy_name);
            const ie = getVal(c.company && c.company.company_state_registry);
            const cnpj = getVal(c.company && c.company.cnpj);
            const cpf = getVal(c.individual && c.individual.cpf);
            const telefone = getVal(c.phone_number || (c.company && c.company.company_phone_number));
            const email = getVal(c.email || (c.company && c.company.company_email));
            
            const cep = getVal(c.address && c.address.postal_code);
            const numero = getVal(c.address && c.address.number);
            const estado = getVal(c.address && ((c.address.state_location && c.address.state_location.name) || c.address.state || c.address.state_location));
            const cidade = getVal(c.address && ((c.address.city_location && c.address.city_location.name) || c.address.city || c.address.city_location));
            const bairro = getVal(c.address && c.address.district);
            const rua = getVal(c.address && c.address.street);
            const complemento = getVal(c.address && c.address.complement);

            return [nome, razao_social, nome_fantasia, ie, cnpj, cpf, telefone, email, cep, numero, estado, cidade, bairro, rua, complemento];
        });

        const d = new Date();
        const dateStr = `${String(d.getDate()).padStart(2, '0')}${String(d.getMonth()+1).padStart(2, '0')}${d.getFullYear()}`;
        downloadCSV(`clientes_${dateStr}.csv`, headers, rows);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    
    window.addEventListener('exportClientCsv', handleExportCSV);
    return () => window.removeEventListener('exportClientCsv', handleExportCSV);
  }, [params]);

  return (
    <React.Fragment>
      {showTitle && (
        <div className="row mb-3">
          <div className="col-12">
            <Typography variant="h6" style={{ color: colors.brandDarkBlue, fontWeight: 600 }}>
              Pesquisar Cliente
            </Typography>
          </div>
        </div>
      )}

      <div className="mb-4">
        <Toggle
          checked={isToggled}
          onChange={handleToggle}
          label={isToggled ? "Pesquisa simplificada" : "Pesquisa padrão"}
        />
      </div>

      <div className="row">
        {fields.map((field) => {
          const isHidden = (isToggled && (field.name === "email" || field.name === "phone_number" || field.name === "end_count"));
          if (isHidden) return null;

          const colClass = "col-md-6 col-xs-12 mb-3";

          if (field.type === "select") {
            return (
              <div key={field.name} className={colClass} data-cy-test={field.cyTest}>
                <Select
                  label={field.label}
                  name={field.name}
                  options={field.options}
                  onChange={onChange}
                  value={params[field.name]}
                  disabled={loading}
                />
              </div>
            );
          }

          return (
            <div key={field.name} className={colClass}>
              <Input
                data-cy-test={field.cyTest}
                name={field.name}
                label={field.label}
                type={field.type}
                maxLength={field.maxLength}
                onChange={onChange}
                value={params[field.name] || ""}
                disabled={loading}
                onEnter={filter}
                placeholder={`Digite o ${field.label.toLowerCase()}...`}
              />
            </div>
          );
        })}

        <div className="col-12 d-flex justify-content-between mt-2 mb-4">
          {modalBehavior ? (
            <Button
              onClick={() => setShowCreateModal(true)}
              disabled={loading}
              variant="secondary"
            >
              Cadastrar Cliente
            </Button>
          ) : (
            <div />
          )}

          <Button
            data-cy-test="#filter-client-button-search"
            onClick={filter}
            disabled={loading}
            variant="primary"
            size="md"
          >
            {loading ? "Pesquisando..." : (
              <React.Fragment>
                <i className="fas fa-search" style={{ marginRight: '8px' }}></i>
                Pesquisar
              </React.Fragment>
            )}
          </Button>
        </div>

        {searchResult.length > 0 && (
          <div className="col-12 mt-4">
            <Typography variant="cardTitle" style={{ color: colors.brandDarkBlue, marginBottom: '1rem', display: 'block' }}>
              Resultados da Pesquisa
            </Typography>
            <Table hover>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Nome</Table.HeaderCell>
                  <Table.HeaderCell>CPF/CNPJ</Table.HeaderCell>
                  <Table.HeaderCell>E-mail</Table.HeaderCell>
                  <Table.HeaderCell>Telefone</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {searchResult.map((result, index) => {
                  let doc = result.company ? result.company.cnpj : (result.individual ? result.individual.cpf : "--");
                  const formattedDoc = formatDocument(doc);
                  const formattedPhone = formatPhoneNumber(result.phone_number);

                  return (
                    <Table.Row
                      key={result.id || index}
                      data-cy-test={`#filter-client-list-result-index=${index}`}
                      onClick={() => onRowClick(result)}
                      hover
                      style={{ cursor: "pointer" }}
                    >
                      <Table.Cell>{result.name}</Table.Cell>
                      <Table.Cell>{formattedDoc}</Table.Cell>
                      <Table.Cell>{result.email}</Table.Cell>
                      <Table.Cell>{formattedPhone || result.phone_number}</Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table>
          </div>
        )}
      </div>
      <Modal onHide={() => setShowCreateModal(false)} show={showCreateModal}>
        <CreateClientContainer
          closeIt={() => setShowCreateModal(false)}
          onSave={(createdClient) => {
            if (onClick && createdClient) {
              onClick(createdClient);
            }
            setShowCreateModal(false);
          }}
        />
      </Modal>
    </React.Fragment>
  );
};

export default FilterClients;



// WEBPACK FOOTER //
// ./src/views/AppViews/Clients/components/Filter/index.jsx